import {
  selectConversation,
  type DurableConversation,
} from "@/api/conversations";
import { updateFrame, type Frame } from "@/api/spaces";
import { durableConversationInputTarget } from "@/lib/durable-conversation-input-target";
import { frameDataToConversationFrame } from "@/lib/conversation-frame-node";
import { frameNodeSize } from "@/lib/frame-node-geometry";
import {
  patchConversationFrameCopies,
  type ConversationFramePatch,
} from "@/lib/conversation-frame-copies";
import { useCanvasStore } from "@/stores/canvas-store";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import { useWorkspaceConversationOverlay } from "@/stores/workspace-conversation-overlay-store";
import {
  flushPendingStreamingUpdates,
  useConversationStore,
} from "@/stores/conversation-store";
import { useFloatingFrameStore } from "@/desktop/floating-frame-store";
import { translate } from "@/i18n";
import { libChatMessages } from "@/i18n/messages/lib-chat";

export function modelSwitchFrame(frameId: string): Frame | null {
  const overlay = useWorkspaceConversationOverlay.getState().overlay;
  if (overlay?.frame.id === frameId) return overlay.frame;
  const node = useCanvasStore
    .getState()
    .nodes.find((item) => item.id === frameId);
  if (node) {
    return node.data.frameType === "conversation"
      ? frameDataToConversationFrame(node.data, {
          ...node.position,
          ...frameNodeSize(node, { width: 400, height: 300 }),
        })
      : null;
  }
  const floating = useFloatingFrameStore.getState().frames[frameId];
  return floating?.frame.frameType === "conversation"
    ? frameDataToConversationFrame(floating.frame, floating.box)
    : null;
}

/** Reuse geometry/chrome, replacing all history and runtime identity metadata. */
export async function rebindModelConversationFrame(
  frame: Frame,
  conversation: DurableConversation,
  shouldContinue: () => boolean,
): Promise<void> {
  const selection = await selectConversation(conversation.id);
  if (!shouldContinue()) return;
  const patch: ConversationFramePatch = {
    title: conversation.title || translate(libChatMessages, "newChat"),
    content: {},
    status: "idle",
    task_id: null,
    metadata: {
      conversationFrame: true,
      ...(frame.metadata.transientConversationFrame
        ? { transientConversationFrame: true }
        : {}),
      conversationId: conversation.id,
      conversationRequiresExactIdentity: true,
      conversationReadOnly: false,
      conversationCanStartNewConversation: true,
      projectAgentId: selection.project_agent_id,
      agentKey: selection.agent_key,
      agentName: conversation.agent_name || selection.agent_key,
      sourceProjectId: conversation.project_id,
    },
  };
  if (!frame.metadata.transientConversationFrame) {
    await updateFrame(conversation.project_id, frame.id, {
      ...patch,
      conversation_id: conversation.id,
    });
    if (!shouldContinue()) return;
  }
  const oldConversationId =
    frame.metadata.conversationId ?? frame.metadata.conversation_id;
  if (typeof oldConversationId === "string") {
    flushPendingStreamingUpdates();
    const store = useConversationStore.getState();
    store.setMessages(
      store.messages.map((message) =>
        !message.conversationId && message.conversationFrameId === frame.id
          ? { ...message, conversationId: oldConversationId }
          : message,
      ),
    );
  }
  patchConversationFrameCopies(frame.id, patch);
  // The user is switching to this chat, and the server keeps one selection
  // per agent, so voice and the standalone input follow it.
  useInputBarTarget
    .getState()
    .setTarget(durableConversationInputTarget(conversation, selection));
}
